import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import ConsultationForm from "../components/ConsultationForm";
import { ArrowRight, ArrowUpRight, CalendarCheck, Clock, MessageSquare, Sparkles } from "lucide-react";
import { BOOKING_URL } from "@/lib/constants";

const EXPECT = [
  {
    icon: MessageSquare,
    title: "Tell us about your project",
    text: "Share your goals, current challenges, and what you need built.",
  },
  {
    icon: Clock,
    title: "We review within one business day",
    text: "Every inquiry is read personally — no auto-responders, no queues.",
  },
  {
    icon: CalendarCheck,
    title: "A focused consultation call",
    text: "We discuss scope, approach, and whether we're the right fit — no pressure.",
  },
];

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("book") ? "book" : "form");

  return (
    <main data-testid="contact-page">
      <Seo
        title="Contact | AT Digital"
        description="Tell AT Digital about your business and book a strategy call — web design, development, storytelling, and automation for service businesses."
      />
      <section className="relative overflow-hidden py-36 sm:py-44">
        <div className="bg-blueprint absolute inset-0 opacity-40" aria-hidden="true" />
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(50% 60% at 80% 10%, rgba(212,175,55,0.08), transparent)" }}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="font-mono text-xs font-semibold tracking-[0.3em] text-gold">CONTACT</p>
                <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl">
                  Let's talk about <span className="text-gold-gradient">your project.</span>
                </h1>
                <p className="mt-6 text-base leading-relaxed text-slate-400">
                  The fastest way to begin is our two-minute project flow — or send us a message directly, whichever
                  suits you.
                </p>
              </Reveal>
              <div className="mt-12 space-y-8">
                {EXPECT.map((e, i) => (
                  <Reveal key={e.title} delay={0.1 + i * 0.1}>
                    <div className="flex gap-4" data-testid={`contact-expect-${i + 1}`}>
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gold/30 bg-navycard">
                        <e.icon className="h-5 w-5 text-gold" strokeWidth={1.75} />
                      </span>
                      <div>
                        <h3 className="font-display text-base font-bold text-white">{e.title}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-slate-400">{e.text}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.45}>
                <div className="mt-12 flex flex-col items-start justify-between gap-6 rounded-2xl border border-gold/30 bg-navycard/60 p-7 sm:flex-row sm:items-center">
                  <div>
                    <h3 className="flex items-center gap-2 font-display text-base font-bold text-white">
                      <Sparkles className="h-4 w-4 text-gold" />
                      Ready to begin?
                    </h3>
                    <p className="mt-1 text-sm text-slate-400">
                      Answer six quick questions and get your project profile instantly.
                    </p>
                  </div>
                  <Link
                    to="/start"
                    data-testid="contact-start-flow-btn"
                    className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-ink transition-colors duration-300 hover:bg-goldlight"
                  >
                    Start My Website
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.15}>
                <div className="mb-6 inline-flex rounded-full border border-navyline bg-navycard p-1.5" role="tablist" aria-label="Contact options">
                  <button
                    role="tab"
                    aria-selected={mode === "form"}
                    data-testid="contact-tab-form"
                    onClick={() => setMode("form")}
                    className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                      mode === "form" ? "bg-gold text-ink" : "text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    Send a Message
                  </button>
                  <button
                    role="tab"
                    aria-selected={mode === "book"}
                    data-testid="contact-tab-book"
                    onClick={() => setMode("book")}
                    className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                      mode === "book" ? "bg-gold text-ink" : "text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    Book a Call
                  </button>
                </div>
                {mode === "form" ? (
                  <ConsultationForm />
                ) : (
                  <div
                    data-testid="booking-embed"
                    className="overflow-hidden rounded-3xl border border-navyline bg-white shadow-2xl shadow-black/40"
                  >
                    <div className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
                      <p className="font-display text-sm font-bold text-ink">Pick a time that works for you</p>
                      <a
                        href={BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="booking-open-external-link"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-royal transition-colors duration-200 hover:text-golddeep"
                      >
                        Open in new tab
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </a>
                    </div>
                    <iframe
                      src={BOOKING_URL}
                      title="Book a consultation call with AT Digital"
                      data-testid="booking-iframe"
                      loading="lazy"
                      className="h-[720px] w-full border-0"
                    />
                  </div>
                )}
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
